import { useCallback } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import AsyncSearchSelectRHF, {
  AsyncSearchSelectProps,
} from '@/components/asyncSelect/AsyncSearchSelectRHF.tsx';
import expensesApi from '@/features/dashboard/expenses/api/expensesApi.ts';
import { expensesApiQueryKeys } from '@/features/dashboard/expenses/api/expensesApi.queryKeys.ts';
import { TourExpenseListDto } from '@/features/dashboard/expenses/types/TourExpenseListDto.ts';
import { remainingToPay } from '@/features/dashboard/expenses/types/TourExpenseDto.utils.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { withSearchQuickFilters } from '@/components/asyncSelect/asyncSelectFilters.ts';
import Chip from '@/components/ui/Chip.tsx';
import { formatPrice } from '@/utils/formatters.ts';

type ExpenseSearchSelectProps<T extends FieldValues> = Omit<
  AsyncSearchSelectProps<T, TourExpenseListDto>,
  'fetchFn' | 'toOption' | 'queryKey'
> & {
  fieldPathName: FieldPath<T>;
  control: Control<T>;
};

/**
 * Wydatki z globalnej listy, bo zapłata nie musi znać wyjazdu z góry. Filtr
 * `hideCompleted` jest tu regułą, nie wygodą: backend odbija zapłatę za wydatek
 * opłacony i anulowany, więc nie ma czego pokazywać. Nie obejmuje jednak
 * rozwiązania opcji po identyfikatorze — inaczej edycja transakcji za wydatek
 * w międzyczasie opłacony pokazywałaby puste pole.
 */
const ExpenseSearchSelectRHF = <T extends FieldValues>(
  props: ExpenseSearchSelectProps<T>,
) => {
  const fetchFn = useCallback(
    (pageRequest: PageRequest, filter: FilterCriteriaDto) =>
      expensesApi.getAllGlobal(
        pageRequest,
        withSearchQuickFilters(filter, { hideCompleted: true }),
      ),
    [],
  );

  const toOption = useCallback((item: TourExpenseListDto) => {
    const title = item.description || item.costCategoryName;
    const price = formatPrice(item.amount.amount, item.amount.currency);
    // „Pozostało” tylko przy częściowej zapłacie: dla nietkniętej faktury byłoby
    // powtórzeniem kwoty, a to jest jedyna linijka, w którą trzeba się wczytać.
    const amounts =
      item.paidAmount > 0
        ? `faktura ${price} · pozostało ${formatPrice(
            remainingToPay(item),
            item.amount.currency,
          )}`
        : `faktura ${price}`;

    return {
      value: item.id,
      // Czytnik ekranu dostaje tę samą treść co oko, tylko tekstem — `label`
      // jest elementem, a `getOptionLabel` musi zwrócić string.
      name: [
        title,
        item.tour.name,
        title === item.costCategoryName ? '' : item.costCategoryName,
        amounts,
      ]
        .filter(Boolean)
        .join(' · '),
      label: (
        <span className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
          <span className="flex min-w-0 max-w-full flex-col gap-1">
            <span className="truncate text-sm font-medium leading-[18px] text-base-text">
              {title}
            </span>
            <span className="flex flex-row flex-wrap items-center gap-1">
              {item.tour.name && <Chip size="sm" label={item.tour.name} />}
              <Chip size="sm" label={item.costCategoryName} />
            </span>
          </span>
          <span className="min-w-0 max-w-full truncate text-xs leading-4 text-neutral-400">
            {amounts}
          </span>
        </span>
      ),
    };
  }, []);

  return (
    <AsyncSearchSelectRHF<T, TourExpenseListDto>
      {...props}
      queryKey={[...expensesApiQueryKeys.global(), 'picker']}
      fetchFn={fetchFn}
      toOption={toOption}
    />
  );
};

export default ExpenseSearchSelectRHF;
